// Schedule controller (ARCHITECTURE §5.2, §6.1 C3). Today's pages/Schedule.tsx
// data logic, with the same loads in the same order, each through
// usePageResource (#1291: latest request wins, friendly error, typed 404):
// - the schedule-featured list, refetched on :id or sport change; each new
//   list re-applies today's selection rule (URL id if featured, else the
//   first: #418);
// - the selected competition's detail (a new pick also resets the venue
//   filter);
// - the season games outside any competition.
// retry() refetches the list. competition.retry() refetches the detail (#414).
//
// #583 divisions: with 2+ live divisions in the sport, one picked division
// narrows the tabs to its competitions (client-side, on `division_id`) and
// the season games to its active seasons; All divisions is today's merge with
// every row and tab tagged. With fewer, nothing here differs from today: no
// param, no extra request, no tag.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { api } from '@/services/api'
import type { DivisionRef } from '@/services/api/types'
import { useSport, useSportContext } from '@/contexts/SportContext'
import { useLeague } from '@/contexts/LeagueContext'
import { useLabels } from '../../labels'
import { divisionRef, useDivision } from '../../division'
import {
  soonestCompetitionId, deriveSchedule, deriveSeasonGames, filterFeaturedByDivision, initialCompetitionId, mergeSeasonGameRows,
  seasonPickerOptions, seasonTournamentsAsFeatured,
  type DivisionTagger, type ScheduleFeaturedItem, type SeasonGameRow, type SeasonTournamentRow,
} from './derive'
import type { SchedulePageVM } from './types'
import { usePageResource } from '../usePageResource'
import { useT } from '@/i18n'
import { siteCoreMessages } from '@/i18n/catalogs/site-core'

export interface ScheduleOptions {
  /** Load the active seasons' games that belong to no competition (`vm.seasonGames`). (#478, #638; every template turns it on). */
  seasonGames?: boolean
}

export function useSchedulePage(venuesAllLabel?: string, opts: ScheduleOptions = {}): SchedulePageVM {
  const words = useT(siteCoreMessages).pages
  const allVenuesLabel = venuesAllLabel ?? words.schedule.allVenues
  const { id } = useParams<{ id: string }>()
  // #652: a picked season (`?season=<id>`); null = today's view.
  const [params, setParams] = useSearchParams()
  const seasonParam = Number(params.get('season'))
  const pickedSeason = Number.isInteger(seasonParam) && seasonParam > 0 ? seasonParam : null
  const pickedSeasonRef = useRef(pickedSeason)
  pickedSeasonRef.current = pickedSeason
  const { activeSportId } = useSportContext()
  const sport = useSport()
  const t = useLabels()
  const { league } = useLeague()
  const division = useDivision()
  // The picked division's id; null = All divisions, or no divisions at all.
  const divId = division.enabled && typeof division.value === 'number' ? division.value : null
  // The selection mode: 'off' (fewer than 2 live divisions: today's rule),
  // else 'all' or the picked division's id; both open on the soonest visible
  // competition (`next_game_at`), else the first.
  const selMode = division.enabled ? String(division.value) : 'off'
  const pickCompetition = (items: ScheduleFeaturedItem[], mode: string, dId: number | null) => {
    const visible = filterFeaturedByDivision(items, dId)
    return mode !== 'off'
      ? soonestCompetitionId(visible, id)
      : initialCompetitionId(visible.map((f) => ({ id: f.tournament_id })), id)
  }
  const live = division.live
  const tag = useMemo<DivisionTagger | undefined>(
    () => (division.enabled ? (id) => divisionRef(live, id) : undefined),
    [division.enabled, live],
  )

  // Root-venue filter; null = all venues.
  const [venueFilter, setVenueFilter] = useState<number | null>(null)

  // The featured list. A sport switch (or the chrome's late stored-sport
  // restore) refetches while the previous sport's request may still be in
  // flight; only the latest answer is applied.
  const featuredRes = usePageResource(
    async () => {
      // #652: a picked season lists its own competitions (finished ones
      // included); today's view is the curated schedule.
      const picked = pickedSeasonRef.current
      return picked != null
        ? seasonTournamentsAsFeatured(
            ((await api.getTournaments(picked)) as { tournaments?: SeasonTournamentRow[] }).tournaments ?? [],
            (league?.seasons ?? []).find((s) => s.id === picked)?.year ?? 0,
          )
        : (((await api.getScheduleFeatured()).featured ?? []) as ScheduleFeaturedItem[])
    },
    [id, activeSportId, pickedSeason],
    { fallback: t.ui.schedule.loadError },
  )
  const featured = useMemo(() => featuredRes.data ?? [], [featuredRes.data])
  const loadingFeatured = featuredRes.loading
  const featuredError = featuredRes.error
  // The selection, derived in render so the detail request goes out in the
  // same commit as the list. Each new list (and each division pick, #583)
  // re-applies the rule, null included: keeping the previous selection would
  // leave the other sport's competition on screen after a switch, and it
  // blocks the no-competitions state (which needs selectedId === null). A
  // fan's pick holds for the list and division it was made on.
  const [manualPick, setManualPick] = useState<{ list: ScheduleFeaturedItem[] | null; mode: string; id: number | null } | null>(null)
  const autoPick = featuredRes.data ? pickCompetition(featuredRes.data, selMode, divId) : null
  const selectedTournamentId = manualPick && manualPick.list === featuredRes.data && manualPick.mode === selMode ? manualPick.id : autoPick

  // The selected competition's detail. Venues are competition-specific, so
  // a new pick resets the venue filter. With nothing picked there is no
  // detail (a previous pick's request is dropped).
  useEffect(() => { setVenueFilter(null) }, [selectedTournamentId])
  const detailRes = usePageResource(
    () => api.getTournament(selectedTournamentId!),
    [selectedTournamentId],
    { enabled: selectedTournamentId != null, notFoundOn404: true, fallback: t.ui.schedule.competitionLoadError },
  )
  const picked = selectedTournamentId != null
  const tournamentData = picked ? detailRes.data : null
  const loadingTournament = picked && detailRes.loading
  const tournamentError = picked ? detailRes.error : null
  const tournamentNotFound = picked && detailRes.notFound

  // Games outside any competition (#478). #638: a sport can run more than one
  // active season (KBA: one is_current, one only is_active). With several,
  // every active season's games are fetched and merged by date, so the
  // non-current season's games are reachable from /schedule too.
  //
  // #653: `/games` goes out at mount, as before #646, in parallel with the
  // season list, so with 0–1 active seasons the request order is fixed again
  // (it raced the featured → /tournaments/N chain while it waited on
  // /seasons). Only when the list shows 2+ active seasons of the resolved
  // sport does it fan out per season. Before the sport resolves (a cold load)
  // the list isn't read at all: that pass is discarded anyway, and an
  // unsported list would fan out to the other sport's games.
  //
  // #583: one picked division lists its own active seasons (the tenant's
  // `active_season_ids`): one season is `/games?division=<id>`, several are
  // merged per season. All divisions is today's merge, rows tagged.
  const wantSeasonGames = Boolean(opts.seasonGames)
  const divisionSeasonIds = division.active?.active_season_ids
  const seasonDivision = useMemo(
    () => new Map((league?.seasons ?? []).map((s) => [s.id, s.division_id ?? null])),
    [league?.seasons],
  )
  const seasonRes = usePageResource(
    () => {
      const rowsOf = (res: unknown) => ((res as { games?: SeasonGameRow[] }).games) ?? []
      // A per-season list's rows carry their season's division when the payload lacks it (tags only).
      // #723: and their season, so a no-division season's games can be named.
      // #652: and always their season, so merged active seasons can be labelled.
      const tagRows = (sid: number) => (rows: SeasonGameRow[]) =>
        tag
          ? rows.map((r) => ({ ...r, season_id: sid, division_id: r.division_id !== undefined ? r.division_id : seasonDivision.get(sid) ?? null }))
          : rows.map((r) => ({ ...r, season_id: sid }))
      const load = async (): Promise<SeasonGameRow[]> => {
        // #652: a picked season: its own games, finished seasons included.
        if (pickedSeason != null) return tagRows(pickedSeason)(rowsOf(await api.getGames(pickedSeason)))
        if (divId != null) {
          const ids = divisionSeasonIds ?? []
          if (ids.length <= 1) return rowsOf(await api.getGames(undefined, undefined, { division: divId }))
          return mergeSeasonGameRows(await Promise.all(ids.map((sid) => api.getGames(sid).then(rowsOf).then(tagRows(sid)))))
        }
        const single = api.getGames()
        // Handled at creation: it can reject while the season list is still
        // pending (an unhandled rejection otherwise). Every path that uses it
        // still awaits `single`, so its error surfaces as before.
        single.catch(() => {})
        if (!activeSportId) return rowsOf(await single)
        const activeIds = await api.getSeasons()
          .then((r) => (r.seasons ?? [])
            .filter((s) => s.is_active && (s.sport_id == null || s.sport_id === activeSportId))
            .map((s) => s.id))
          .catch(() => [] as number[])
        if (activeIds.length <= 1) return rowsOf(await single)
        const lists = await Promise.all(activeIds.map((sid) => api.getGames(sid).then(rowsOf).then(tagRows(sid))))
        return mergeSeasonGameRows(lists)
      }
      return load()
    },
    // `tag`, the season map and the division's season ids follow the tenant
    // payload; the division pick (divId) is what refetches.
    [activeSportId, divId, pickedSeason],
    { enabled: wantSeasonGames },
  )
  const seasonRows = useMemo(() => seasonRes.data ?? [], [seasonRes.data])
  const seasonStatus: 'idle' | 'loading' | 'ready' | 'error' = !wantSeasonGames ? 'idle' : seasonRes.loading ? 'loading' : seasonRes.error ? 'error' : 'ready'
  // #723: a no-division season's games carry the season's name as their tag.
  const seasonTag = useMemo(() => {
    const byId = new Map((league?.seasons ?? []).map((s) => [s.id, s]))
    return (sid: number): DivisionRef | null => {
      const s = byId.get(sid)
      return s && s.division_id == null && s.name ? { id: -s.id, name: s.name, slug: '', short_name: null } : null
    }
  }, [league?.seasons])
  // #652: several active seasons merged (no divisions): every game names its season.
  const seasonName = useMemo(() => {
    const byId = new Map((league?.seasons ?? []).map((s) => [s.id, s]))
    return (sid: number): DivisionRef | null => {
      const s = byId.get(sid)
      return s?.name ? { id: -s.id, name: s.name, slug: '', short_name: null } : null
    }
  }, [league?.seasons])
  const seasonGames = useMemo(
    () => deriveSeasonGames(seasonRows, tag, seasonTag, pickedSeason == null ? seasonName : undefined),
    [seasonRows, tag, seasonTag, seasonName, pickedSeason],
  )

  // #652: the season picker lists the sport's public seasons from the tenant
  // payload (active and finished; hidden and utility seasons are never in
  // it), so it adds no request.
  const pickerSeasons = useMemo(
    () => (league?.seasons && activeSportId ? league.seasons.filter((s) => s.sport_id == null || s.sport_id === activeSportId) : null),
    [league?.seasons, activeSportId],
  )
  const setSeason = useCallback((v: number | null) => {
    setParams((p) => {
      const next = new URLSearchParams(p)
      if (v == null) next.delete('season')
      else next.set('season', String(v))
      return next
    }, { replace: true })
  }, [setParams])
  // A season that isn't (or is no longer) public for this sport, or not in
  // the picked division, falls back to today's view.
  const seasonOptions = useMemo(
    () => seasonPickerOptions(
      pickerSeasons ?? [],
      divId,
      (active) => (active > 1 ? t.ui.schedule.currentSeasons : t.ui.schedule.currentSeason),
      (dId) => live.find((d) => d.id === dId)?.name ?? null,
    ),
    [pickerSeasons, divId, t, live],
  )
  useEffect(() => {
    if (pickedSeason == null || pickerSeasons == null) return
    if (!seasonOptions.some((o) => o.value === pickedSeason)) setSeason(null)
  }, [pickedSeason, pickerSeasons, seasonOptions, setSeason])
  // A sport switch starts from today's view (the season belongs to the old sport).
  const lastSportRef = useRef(activeSportId)
  useEffect(() => {
    if (lastSportRef.current === activeSportId) return
    const had = lastSportRef.current
    lastSportRef.current = activeSportId
    if (had && pickedSeasonRef.current != null) setSeason(null)
  }, [activeSportId, setSeason])
  const retrySeasonGames = seasonRes.retry

  const retry = featuredRes.retry
  const retryCompetition = detailRes.retry
  const selectCompetition = useCallback((v: number | null) => setManualPick({ list: featuredRes.data, mode: selMode, id: v }), [featuredRes.data, selMode])
  const selectVenue = useCallback((v: number | null) => setVenueFilter(v), [])

  const shownFeatured = useMemo(() => filterFeaturedByDivision(featured, divId), [featured, divId])
  const d = useMemo(() => deriveSchedule({
    sport,
    featured: shownFeatured,
    loadingFeatured,
    featuredError,
    selectedId: selectedTournamentId,
    details: tournamentData,
    loadingDetails: loadingTournament,
    detailsError: tournamentError,
    detailsNotFound: tournamentNotFound,
    venueFilter,
    allVenuesLabel,
    divisionTag: tag,
  }), [sport, shownFeatured, loadingFeatured, featuredError, selectedTournamentId, tournamentData, loadingTournament, tournamentError, tournamentNotFound, venueFilter, allVenuesLabel, tag])

  return {
    ...d,
    retry,
    competitions: { ...d.competitions, set: selectCompetition },
    competition: { ...d.competition, retry: retryCompetition },
    venueFilter: { ...d.venueFilter, set: selectVenue },
    seasonGames: { status: seasonStatus, days: seasonGames.days, total: seasonGames.total, retry: retrySeasonGames, seasonTagged: seasonGames.seasonTagged },
    division,
    season: {
      value: pickedSeason,
      options: seasonOptions,
      set: setSeason,
      visible: seasonOptions.length > 2 || pickedSeason != null,
    },
  }
}
